import {ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {socialFeedApi} from '../../../../services/socialFeedApi';
import {
  MAX_POST_IMAGE_COUNT,
  MAX_POST_IMAGE_SIZE_BYTES,
  MAX_POST_IMAGE_SOURCE_SIZE_BYTES,
  SUPPORTED_POST_IMAGE_TYPES,
} from '../constants';
import {isRequestCanceled, optimizePostImageFile} from '../utils';

export const useCreatePostComposer = (
  token: string | null,
  refreshPosts: () => Promise<void>,
) => {
  const createPostRequestControllerRef = useRef<AbortController | null>(null);
  const createPostTextareaRef = useRef<HTMLTextAreaElement>(null);
  const createPostFileInputRef = useRef<HTMLInputElement>(null);
  const activeUploadSessionIdsRef = useRef<string[]>([]);

  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedPostImageFiles, setSelectedPostImageFiles] = useState<File[]>([]);
  const [selectedPostImagePreviewUrls, setSelectedPostImagePreviewUrls] = useState<string[]>([]);
  const [createPostLoading, setCreatePostLoading] = useState(false);
  const [createPostError, setCreatePostError] = useState<string | null>(null);
  const [createPostValidationError, setCreatePostValidationError] = useState<string | null>(null);
  const [createPostStatusMessage, setCreatePostStatusMessage] = useState<string | null>(null);
  const [createPostUploadProgressPercent, setCreatePostUploadProgressPercent] = useState<number | null>(null);

  const abortPendingUploadSessions = useCallback(async (uploadSessionIds: string[]) => {
    if (!token || uploadSessionIds.length === 0) {
      return;
    }

    await Promise.allSettled(uploadSessionIds.map(async (uploadSessionId) => {
      try {
        await socialFeedApi.abortMediaUploadSession(token, uploadSessionId);
      } catch (cleanupError) {
        console.error('Failed to abort pending post upload session:', cleanupError);
      }
    }));

    activeUploadSessionIdsRef.current = activeUploadSessionIdsRef.current.filter((activeUploadSessionId) => (
      !uploadSessionIds.includes(activeUploadSessionId)
    ));
  }, [token]);

  const clearSelectedPostImages = useCallback(() => {
    setSelectedPostImageFiles([]);
    if (createPostFileInputRef.current) {
      createPostFileInputRef.current.value = '';
    }
  }, []);

  const removeSelectedPostImage = useCallback((imageIndex: number) => {
    setSelectedPostImageFiles((currentFiles) => currentFiles.filter((_, currentIndex) => currentIndex !== imageIndex));
    setCreatePostError(null);
    setCreatePostValidationError(null);

    if (createPostFileInputRef.current) {
      createPostFileInputRef.current.value = '';
    }
  }, []);

  const resetComposerState = useCallback(() => {
    setNewPostContent('');
    clearSelectedPostImages();
    setCreatePostLoading(false);
    setCreatePostError(null);
    setCreatePostValidationError(null);
    setCreatePostStatusMessage(null);
    setCreatePostUploadProgressPercent(null);
  }, [clearSelectedPostImages]);

  const closeCreatePostModal = useCallback(() => {
    const activeUploadSessionIds = [...activeUploadSessionIdsRef.current];

    createPostRequestControllerRef.current?.abort();
    createPostRequestControllerRef.current = null;
    activeUploadSessionIdsRef.current = [];
    setIsCreatePostModalOpen(false);
    resetComposerState();

    if (activeUploadSessionIds.length > 0) {
      void abortPendingUploadSessions(activeUploadSessionIds);
    }
  }, [abortPendingUploadSessions, resetComposerState]);

  const openCreatePostModal = useCallback(() => {
    setIsCreatePostModalOpen(true);
    resetComposerState();
  }, [resetComposerState]);

  const openCreatePostFileBrowser = useCallback(() => {
    createPostFileInputRef.current?.click();
  }, []);

  const handleSelectedPostImageChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    if (selectedPostImageFiles.length + selectedFiles.length > MAX_POST_IMAGE_COUNT) {
      setCreatePostError(null);
      setCreatePostValidationError(`You can upload up to ${MAX_POST_IMAGE_COUNT} images per post.`);
      return;
    }

    for (const selectedFile of selectedFiles) {
      if (!SUPPORTED_POST_IMAGE_TYPES.includes(selectedFile.type)) {
        setCreatePostError(null);
        setCreatePostValidationError('Only JPG, PNG, and WEBP images are supported.');
        return;
      }

      if (selectedFile.size > MAX_POST_IMAGE_SOURCE_SIZE_BYTES) {
        setCreatePostError(null);
        setCreatePostValidationError('Each image must be 40 MB or less.');
        return;
      }
    }

    setSelectedPostImageFiles((currentFiles) => [...currentFiles, ...selectedFiles]);
    setCreatePostError(null);
    setCreatePostValidationError(null);
  }, [selectedPostImageFiles.length]);

  const handleCreatePostSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setCreatePostError('You must be signed in to create a post.');
      return;
    }

    const trimmedContent = newPostContent.trim();

    if (!trimmedContent) {
      setCreatePostValidationError('Post content is required.');
      setCreatePostError(null);
      return;
    }

    if (trimmedContent.length > 5000) {
      setCreatePostValidationError('Post content must be 5000 characters or less.');
      setCreatePostError(null);
      return;
    }

    createPostRequestControllerRef.current?.abort();
    const controller = new AbortController();
    createPostRequestControllerRef.current = controller;
    const requestSignal = controller.signal;
    const uploadSessionIds: string[] = [];

    try {
      setCreatePostLoading(true);
      setCreatePostError(null);
      setCreatePostValidationError(null);
      setCreatePostStatusMessage(selectedPostImageFiles.length > 0 ? 'Preparing images...' : 'Publishing post...');
      setCreatePostUploadProgressPercent(selectedPostImageFiles.length > 0 ? 0 : null);

      const optimizedImageFiles: File[] = [];

      for (let imageIndex = 0; imageIndex < selectedPostImageFiles.length; imageIndex += 1) {
        const selectedPostImageFile = selectedPostImageFiles[imageIndex];

        setCreatePostStatusMessage(
          selectedPostImageFiles.length === 1
            ? 'Preparing image...'
            : `Preparing image ${imageIndex + 1} of ${selectedPostImageFiles.length}...`
        );

        const optimizedImageFile = await optimizePostImageFile(selectedPostImageFile, MAX_POST_IMAGE_SIZE_BYTES);

        if (requestSignal.aborted) {
          return;
        }

        optimizedImageFiles.push(optimizedImageFile);
      }

      const totalUploadBytes = optimizedImageFiles.reduce((sum, file) => sum + file.size, 0);
      let uploadedBytes = 0;

      for (let imageIndex = 0; imageIndex < optimizedImageFiles.length; imageIndex += 1) {
        const optimizedImageFile = optimizedImageFiles[imageIndex];

        setCreatePostStatusMessage(
          optimizedImageFiles.length === 1
            ? 'Starting secure upload...'
            : `Starting secure upload for image ${imageIndex + 1} of ${optimizedImageFiles.length}...`
        );

        const uploadSessionData = await socialFeedApi.createMediaUploadSession(token, {
          purpose: 'post-image',
          fileName: optimizedImageFile.name,
          contentType: optimizedImageFile.type,
          fileSize: optimizedImageFile.size,
        }, requestSignal);

        if (requestSignal.aborted) {
          return;
        }

        uploadSessionIds.push(uploadSessionData.uploadSession.id);
        activeUploadSessionIdsRef.current = [...uploadSessionIds];
        const uploadedBytesBeforeCurrentFile = uploadedBytes;

        setCreatePostStatusMessage(
          optimizedImageFiles.length === 1
            ? 'Uploading image to secure storage...'
            : `Uploading image ${imageIndex + 1} of ${optimizedImageFiles.length}...`
        );

        await socialFeedApi.uploadSinglePutSessionFile(uploadSessionData, optimizedImageFile, {
          signal: requestSignal,
          onProgress: (fraction) => {
            const currentFileUploadedBytes = fraction * optimizedImageFile.size;
            const progressFraction = totalUploadBytes > 0
              ? (uploadedBytesBeforeCurrentFile + currentFileUploadedBytes) / totalUploadBytes
              : 0;
            setCreatePostUploadProgressPercent(Math.round(progressFraction * 100));
          },
        });

        if (requestSignal.aborted) {
          return;
        }

        uploadedBytes += optimizedImageFile.size;
      }

      setCreatePostStatusMessage('Publishing post...');
      setCreatePostUploadProgressPercent(uploadSessionIds.length > 0 ? 100 : null);

      await socialFeedApi.createPost(token, {
        content: trimmedContent,
        uploadSessionIds: uploadSessionIds.length > 0 ? uploadSessionIds : undefined,
      }, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      activeUploadSessionIdsRef.current = [];
      setIsCreatePostModalOpen(false);
      resetComposerState();
      await refreshPosts();
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      await abortPendingUploadSessions(uploadSessionIds);
      setCreatePostError(error instanceof Error ? error.message : 'Failed to create post');
      setCreatePostStatusMessage(null);
      setCreatePostUploadProgressPercent(null);
    } finally {
      if (!requestSignal.aborted) {
        setCreatePostLoading(false);
      }

      if (createPostRequestControllerRef.current?.signal === requestSignal) {
        createPostRequestControllerRef.current = null;
      }
    }
  }, [
    abortPendingUploadSessions,
    newPostContent,
    refreshPosts,
    resetComposerState,
    selectedPostImageFiles,
    token,
  ]);

  useEffect(() => {
    if (selectedPostImageFiles.length === 0) {
      setSelectedPostImagePreviewUrls([]);
      return;
    }

    const previewUrls = selectedPostImageFiles.map((selectedPostImageFile) => URL.createObjectURL(selectedPostImageFile));
    setSelectedPostImagePreviewUrls(previewUrls);

    return () => {
      previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    };
  }, [selectedPostImageFiles]);

  useEffect(() => {
    if (!isCreatePostModalOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      createPostTextareaRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [isCreatePostModalOpen]);

  useEffect(() => () => {
    createPostRequestControllerRef.current?.abort();
    const activeUploadSessionIds = [...activeUploadSessionIdsRef.current];
    activeUploadSessionIdsRef.current = [];

    if (activeUploadSessionIds.length > 0) {
      void abortPendingUploadSessions(activeUploadSessionIds);
    }
  }, [abortPendingUploadSessions]);

  return {
    isCreatePostModalOpen,
    newPostContent,
    selectedPostImageFiles,
    selectedPostImagePreviewUrls,
    createPostLoading,
    createPostError,
    createPostValidationError,
    createPostStatusMessage,
    createPostUploadProgressPercent,
    createPostTextareaRef,
    createPostFileInputRef,
    setNewPostContent,
    clearSelectedPostImages,
    removeSelectedPostImage,
    closeCreatePostModal,
    openCreatePostModal,
    openCreatePostFileBrowser,
    handleSelectedPostImageChange,
    handleCreatePostSubmit,
  };
};
